const GEO_TIMEOUT_PRIMARY_MS = 8000;
const GEO_TIMEOUT_FALLBACK_MS = 12000;

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateTime = (date) => {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  const seconds = pad2(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const getCurrentPositionPromise = (options) =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

const getCoordinates = async () => {
  if (!navigator.geolocation) {
    return { lat: null, lng: null, reason: "unsupported" };
  }

  if (!window.isSecureContext) {
    console.warn("Geolocation requires a secure context (HTTPS/localhost).");
    return { lat: null, lng: null, reason: "insecure_context" };
  }

  try {
    const precisePosition = await getCurrentPositionPromise({
      enableHighAccuracy: true,
      timeout: GEO_TIMEOUT_PRIMARY_MS,
      maximumAge: 0,
    });
    return {
      lat: precisePosition.coords.latitude,
      lng: precisePosition.coords.longitude,
      reason: null,
    };
  } catch (primaryError) {
    console.warn("High-accuracy geolocation failed, trying fallback.", primaryError);
    if (primaryError && primaryError.code === 1) {
      return { lat: null, lng: null, reason: "permission_denied" };
    }
  }

  try {
    const fallbackPosition = await getCurrentPositionPromise({
      enableHighAccuracy: false,
      timeout: GEO_TIMEOUT_FALLBACK_MS,
      maximumAge: 300000,
    });
    return {
      lat: fallbackPosition.coords.latitude,
      lng: fallbackPosition.coords.longitude,
      reason: null,
    };
  } catch (fallbackError) {
    console.warn("Fallback geolocation failed.", fallbackError);
    if (fallbackError && fallbackError.code === 1) {
      return { lat: null, lng: null, reason: "permission_denied" };
    }
    return { lat: null, lng: null, reason: "unavailable" };
  }
};

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

export const stampPhotoWithDateLocation = async (
  imageDataUrl,
  capturedAt = new Date()
) => {
  const timestampText = formatDateTime(
    capturedAt instanceof Date ? capturedAt : new Date(capturedAt)
  );
  const coords = await getCoordinates();
  const coordsText = coords
    ? coords.lat != null && coords.lng != null
      ? `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
      : coords.reason === "permission_denied"
      ? "No location permission"
      : "Location unavailable"
    : "Location unavailable";

  const sourceImage = await loadImage(imageDataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = sourceImage.width;
  canvas.height = sourceImage.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return imageDataUrl;
  }
  ctx.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);

  const fontSize = Math.max(14, Math.round(canvas.width * 0.02));
  const lineHeight = Math.round(fontSize * 1.25);
  const padding = Math.max(8, Math.round(canvas.width * 0.01));
  const textLines = [timestampText, coordsText];

  ctx.font = `${fontSize}px Arial`;
  const textWidths = textLines.map((line) => ctx.measureText(line).width);
  const boxWidth = Math.ceil(Math.max(...textWidths) + padding * 2);
  const boxHeight = Math.ceil(lineHeight * textLines.length + padding * 2);
  const boxX = canvas.width - boxWidth - padding;
  const boxY = padding;

  ctx.fillStyle = "rgba(0, 0, 0, 0.60)";
  ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  textLines.forEach((line, index) => {
    ctx.fillText(line, boxX + padding, boxY + padding + index * lineHeight);
  });

  return canvas.toDataURL("image/jpeg", 0.9);
};
